import { statusKind, parseAmount, additiveName } from '../dashboardTheme.js'
import { looksLikeCountries } from '../countries.js'

/**
 * What each column of a product's table holds, read from the cells themselves — so a table the
 * product describes differently still gets the right pictures, and the product sends nothing extra.
 *
 *   country   names of countries                       -> map, regions, mosaic, flow
 *   region    a column headed "Region"                 -> used instead of our own region table
 *   status    Ready / Partly / Missing, Fresh / Stale… -> the status board
 *   amount    figures (counts, money, percentages)     -> what the pictures measure
 *   year      figures under year-like headings (2022, FY2023, 2022-23) -> the ranking over time
 *   text      anything else; the first one names the rows
 */
const SKIP = /^#$|\bid\b|rank|^no\.?$/i
const YEAR = /^(fy\s*)?(19|20)\d{2}(\s*[-/]\s*\d{2,4})?$|^q[1-4]\s*(19|20)\d{2}$/i
const TOTAL = /\btotal\b|\boverall\b|\ball\b|\bsum\b/i

export function profileTable(columns, rows) {
  const cols = Array.isArray(columns) ? columns : []
  const body = Array.isArray(rows) ? rows.filter(Array.isArray) : []
  const width = Math.max(cols.length, ...body.map(r => r.length), 0)
  const cell = (r, c) => r[c]
  const out = { countryCol: -1, regionCol: -1, nameCol: -1, statusCols: [], metrics: [], yearCols: [] }

  for (let c = 0; c < width; c++) {
    const head = String(cols[c] ?? '').trim()
    const cells = body.map(r => cell(r, c))
    const filled = cells.filter(v => v !== null && v !== undefined && String(v).trim() !== '' && String(v).trim() !== '—')
    if (!filled.length) continue
    if (filled.length >= 2 && filled.every(v => statusKind(v))) { out.statusCols.push(c); continue }
    if (/region/i.test(head) && filled.every(v => typeof v === 'string')) { if (out.regionCol < 0) out.regionCol = c; continue }
    if (out.countryCol < 0 && looksLikeCountries(cells, Math.min(4, Math.max(3, body.length)))) {
      out.countryCol = c
      if (out.nameCol < 0) out.nameCol = c
      continue
    }
    const parsed = filled.map(parseAmount)
    if (filled.length >= 2 && parsed.every(Boolean) && !SKIP.test(head)) {
      const year = YEAR.test(head)
      if (year) out.yearCols.push(c)
      else if (/year|date|week|month|period/i.test(head)) continue
      const percent = parsed.some(p => p.percent)
      const prefix = parsed.find(p => p.prefix)?.prefix || ''
      out.metrics.push({
        c, header: head || `Column ${c + 1}`, prefix, percent, year,
        additive: !percent && additiveName(head),
        total: TOTAL.test(head),
        values: cells.map(v => parseAmount(v)?.n ?? null),
      })
      continue
    }
    if (out.nameCol < 0 && filled.every(v => typeof v === 'string' || typeof v === 'number')) out.nameCol = c
  }
  return out
}

/** The figure columns that are parts of one whole, so they can be the sources of a flow — two or
 *  more addable columns of the same kind (all money or all counts), with any "Total" left out
 *  because it would count everything twice. Empty when there are not two such columns. */
export function partColumns(metrics) {
  const parts = metrics.filter(m => m.additive && !m.total && !m.year)
  if (parts.length < 2) return []
  const money = parts.filter(m => m.prefix)
  if (money.length && money.length !== parts.length) return []
  return parts
}
